import { cosmeticsFor, type CosmeticItem } from '@/config/cosmetics';
import { el } from '@/ui/dom';
import { flagCanvas } from '@/entities/carLook';
import { dragonCardCanvas } from '@/entities/livery/dragon';
import { kitCardCanvas } from '@/entities/livery/jersey';
import { wheelCardCanvas } from '@/entities/livery/wheels';

/**
 * cosmeticArt.ts (M87) — shared cosmetic-card bits for THE SHOP and the
 * garage CUSTOMIZE overlay: card art (kit shirt / flag / tire disc, the same
 * painters that skin the car), the inspiration type tag, and the merged
 * PAINT JOBS filter model (kits + flag wraps are one body slot since M87).
 */

export type PaintJobFilter = 'all' | 'kits' | 'flags';

export const PAINTJOB_FILTERS: { key: PaintJobFilter; label: string }[] = [
  { key: 'all', label: 'ALL' },
  { key: 'kits', label: 'KITS' },
  { key: 'flags', label: 'FLAGS' },
];

/** The merged PAINT JOBS rail: kits then flags, narrowed by the filter. */
export function paintJobItems(filter: PaintJobFilter): CosmeticItem[] {
  const kits = cosmeticsFor('paint');
  const flags = cosmeticsFor('wrap');
  return filter === 'kits' ? kits : filter === 'flags' ? flags : [...kits, ...flags];
}

/** What inspired the item — the merged-shop sorting language (owner spec:
 *  "flags, kits, boots… based on the inspiration"). */
export function cosmeticTypeTag(item: CosmeticItem): string {
  if (item.category === 'mod') return item.mod?.motion ? 'MOD · MOVES' : 'MOD';
  if (item.category === 'paint') return 'KIT';
  if (item.category === 'wrap') return 'FLAG';
  // M144: a painted wheel (Dragon Claws) is not a boot colourway.
  return item.tires?.art ? 'WHEELS' : 'BOOTS';
}

/** M128 — one line icon per mod kind for the shop cards (stroke = the
 *  card's rarity colour via currentColor). */
const MOD_ICONS: Record<string, string> = {
  gloves: '<path d="M7 20V9a2 2 0 0 1 4 0v3M11 9V5a2 2 0 0 1 4 0v7M15 8V6a2 2 0 0 1 4 0v9a6 6 0 0 1-6 6h-2a4 4 0 0 1-4-4v-2"/><path d="M7 12l-2-1.5a1.6 1.6 0 0 0-2 2.4L7 17"/>',
  floods: '<path d="M6 21l3-13h6l3 13M8 13h8M7 17h10"/><rect x="7" y="3" width="10" height="5" rx="1"/><path d="M4 9l2-2M20 9l-2-2"/>',
  kitnumber: '<path d="M6 8l2-2v12M13 6h4a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z"/>',
  cleats: '<path d="M4 15h16v3H4z"/><path d="M6 15V9M10 15V8M14 15V8M18 15V9"/><path d="M6 9l-1.5-3M10 8l-1-3M14 8l1-3M18 9l1.5-3"/>',
  whistle: '<path d="M14 6h5a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-4"/><circle cx="9" cy="14" r="5"/><path d="M14 6v6M17 5V3"/>',
  rally: '<path d="M3 12h18"/><circle cx="6" cy="8" r="2.5"/><circle cx="12" cy="8" r="2.5"/><circle cx="18" cy="8" r="2.5"/><path d="M6 12v4M18 12v4"/>',
  trophy: '<path d="M8 4h8v5a4 4 0 0 1-8 0V4z"/><path d="M8 6H5a2 2 0 0 0 2 4h1M16 6h3a2 2 0 0 1-2 4h-1"/><path d="M12 13v3M9 20h6M10 16h4v4h-4z"/>',
  flags: '<path d="M6 21V4M6 4h10l-2 3 2 3H6"/><path d="M16 21v-6"/>',
  goalwing: '<path d="M4 20V8h16v12"/><path d="M4 8l3 4h10l3-4"/><path d="M8 20v-8M12 20v-8M16 20v-8M4 16h16M4 12h16"/>',
  mudflaps: '<circle cx="12" cy="8" r="5"/><path d="M5 8h14l-2 12H7z"/>',
  ponytail: '<circle cx="12" cy="7" r="4"/><path d="M14 10c3 2 5 5 5 9M13 11c2 3 3 6 2 9M11 11c0 3 0 6-2 9"/>',
  tifo: '<path d="M4 5h16"/><path d="M6 5v10q3 3 6 0t6 0V5"/><circle cx="12" cy="10" r="2.5"/>',
  fans: '<circle cx="8" cy="7" r="2.5"/><circle cx="16" cy="7" r="2.5"/><path d="M4 21v-6a4 4 0 0 1 8 0v6M12 21v-6a4 4 0 0 1 8 0v6"/><path d="M6 12V9M18 12V9"/>',
};

/** Append a cosmetic card's art block (shared shop-card classes). */
export function cosmeticCardArt(item: CosmeticItem, parent: HTMLElement): void {
  if (item.paint) {
    // M144: kits are drawn as the JERSEY they come from — the shirt alone,
    // floating at a slight tilt — at 3× the tile so the trim stays crisp on
    // retina phones. The canvas is transparent: the card's own glow shows.
    // The dragon is not a shirt: its card is a piece of the wrap itself.
    const mini = item.paint.pattern === 'dragon' ? dragonCardCanvas(360, 240) : kitCardCanvas(item.paint, 360, 240);
    mini.className = 'shop-card-flag shop-card-kit';
    parent.appendChild(mini);
  } else if (item.flag) {
    const mini = flagCanvas(item.flag, 120, 80);
    mini.className = 'shop-card-flag';
    parent.appendChild(mini);
  } else if (item.mod) {
    // M128: a line icon on a glass tile in the tier colour.
    const tile = el('div', 'shop-card-mod', parent);
    tile.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      (MOD_ICONS[item.mod.kind] ?? '') +
      '</svg>'; // static SVG constants
  } else if (item.tires?.art) {
    // M144: a painted wheel shows its own face on the card.
    const mini = wheelCardCanvas(360, 240);
    mini.className = 'shop-card-flag shop-card-kit';
    parent.appendChild(mini);
  } else if (item.tires) {
    const disc = el('div', 'shop-card-tire', parent);
    const rubber = `#${item.tires.tyre.toString(16).padStart(6, '0')}`;
    // M114: metal-rubber sets (Golden Boot / Quicksilver) layer a soft
    // specular highlight over the base colour so the card reads "polished
    // metal", not flat paint. Pure inline CSS — no ui.css change needed.
    disc.style.background = item.tires.metal
      ? `radial-gradient(circle at 32% 28%, rgba(255,255,255,0.6), rgba(255,255,255,0) 58%), ${rubber}`
      : rubber;
    disc.style.borderColor = `#${item.tires.rim.toString(16).padStart(6, '0')}`;
  }
}
